import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import {ChangeEvent, useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import CheckIcon from '@mui/icons-material/Check';
import {translations} from '../../../../../../i18n/translations';
import {PendingTask, formatPendingTask} from './format-pending-task';
import {CardCollapse} from '../../../../../../components/shared/card-collapse';
import {EntityPendingTask} from '../../../../../../backend/interfaces/entity-pending-task';
import {useEntity} from '../../../../../../contexts/entity/context';
import {convertArrayToIndex} from '../../../../../../utils/functions';
import {EntitySocialResponse} from '../../../../../../backend/interfaces/entity-social-response';
import {CustomCheckbox} from '../../../../../../components/checkbox/checkbox';
import {useAppContext} from '../../../../../../contexts/app/context';
import {CareTask} from '../../../../../../backend/interfaces/care-task';
import {
  BADL_SECTOR_ID,
  CLINICAL_SUPPORT_SECTOR_ID,
  IADL_SECTOR_ID,
  PERSONAL_DEVELOPMENT_SECTOR_ID,
} from '../../../../../../utils/constants';
import {createUsersFilters} from '../../../../../../components/users-filters/users-filters-utils';

type PendingTaskData = {pendingCareTask: EntityPendingTask; careTask: CareTask};

type PendingTasksProps = {
  loading: boolean;
  data: PendingTaskData[];
  from: string;
  rawFilters: string;
  userFilter: string;
  canWrite: {
    calendar: boolean;
    badl: boolean;
    iadl: boolean;
    clinicalSupport: boolean;
    personalDevelopment: boolean;
  };
  onCancel: () => void;
  onValidateAllTasks: (tasks: {[id: string]: PendingTask}, selected: string[]) => void;
  onPendingClick: (id: string) => void;
};

type DayPendingTasks = {
  [day: string]: PendingTask[];
};

export function PendingTasks({
  loading,
  data,
  from,
  rawFilters,
  userFilter,
  canWrite,
  onCancel,
  onValidateAllTasks,
  onPendingClick,
}: PendingTasksProps) {
  const {t} = useTranslation();
  const {state: entityState} = useEntity();
  const {
    state: {
      currentThemeSettings: {headerHeight},
    },
  } = useAppContext();
  let {entityId}: {entityId: string} = useParams();
  const [tasks, setTasks] = useState<{[id: string]: PendingTask} | undefined>(undefined);
  const [dayTasks, setDayTasks] = useState<DayPendingTasks | undefined>(undefined);
  const [socialResponses] = useState<{[id: string]: EntitySocialResponse}>(
    convertArrayToIndex(entityState.availableSocialResponses, 'id')
  );
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [filters, setFilters] = useState<string[] | undefined>(undefined);
  const formatting = useRef<boolean>(false);
  const [canWriteSelected, setCanWriteSelected] = useState<boolean>(false);

  useEffect(() => {
    if (rawFilters) {
      const filtersArray = rawFilters.split('&').map((s) => s.split('=')[1]);
      setFilters(filtersArray);
    } else {
      setFilters(undefined);
    }
  }, [rawFilters]);

  useEffect(() => {
    if (tasks) {
      setSelectedTasks(Object.keys(tasks).map((key) => tasks[key].id));
      const filtered = Object.keys(tasks).filter((key) => {
        if (tasks[key].selected && canWrite.calendar) {
          if (
            (tasks[key].sectorId === BADL_SECTOR_ID && canWrite.badl) ||
            (tasks[key].sectorId === IADL_SECTOR_ID && canWrite.iadl) ||
            (tasks[key].sectorId === CLINICAL_SUPPORT_SECTOR_ID && canWrite.clinicalSupport) ||
            (tasks[key].sectorId === PERSONAL_DEVELOPMENT_SECTOR_ID && canWrite.personalDevelopment)
          ) {
            return true;
          }
        }
        return false;
      });
      setCanWriteSelected(filtered.length > 0);
    }
  }, [canWrite, tasks]);

  useEffect(() => {
    const formatData = async () => {
      const formattedTasks = await formatPendingTask(entityId, data);
      setTasks(formattedTasks);
    };

    if (!loading && !formatting.current) {
      formatting.current = true;
      formatData().finally(() => {
        formatting.current = false;
      });
    }
  }, [data, entityId, loading]);

  useEffect(() => {
    if (tasks) {
      if (Object.keys(tasks).length === 0) {
        setDayTasks({});
        return;
      }

      setDayTasks(undefined);
      const days: DayPendingTasks = {};
      const filters = createUsersFilters(rawFilters);

      Object.keys(tasks).forEach((key) => {
        const date = moment(tasks[key].date).format('ddd, DD MMM YYYY');
        const task = tasks[key];

        // -- Filter by user --
        if (userFilter !== '') {
          if (task.userId !== userFilter) {
            return;
          }
        }
        // -- Filter by careTask Status --
        if (filters.socialResponses && filters.socialResponses.length > 0) {
          if (!filters.socialResponses?.some((sr) => task.socialResponses.includes(sr))) {
            return;
          }
        }
        // -- Filter by shifts --
        if (filters.shifts && filters.shifts.length > 0) {
          if (!filters.shifts.some((shift) => task.entityShiftIds?.includes(shift))) {
            return;
          }
        }

        days[date] = [...(days[date] || []), tasks[key]];
      });
      setDayTasks(days);
    }
  }, [rawFilters, tasks, userFilter]);

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (!tasks) return;
    if (event.target.checked) {
      setSelectedTasks(Object.keys(tasks).map((key) => tasks[key].id));
    } else {
      setSelectedTasks([]);
    }
  };

  const handleSelectDay = (event: ChangeEvent<HTMLInputElement>, day: string) => {
    if (!dayTasks) return;
    if (event.target.checked) {
      setSelectedTasks([...selectedTasks, ...(dayTasks[day] || []).map((task) => task.id)]);
    } else {
      setSelectedTasks(
        selectedTasks.filter((id) => !(dayTasks[day] || []).map((task) => task.id).includes(id))
      );
    }
  };

  let countTasks = 0;
  const topSpacing = headerHeight + 16 + 15 + 16 + 108;

  if (loading || !tasks || !dayTasks) {
    return (
      <Card
        sx={{
          maxHeight: `calc(100vh - ${topSpacing}px)`,
        }}
        elevation={0}
        className="pending-tasks-card"
      >
        {new Array(6).fill(1).map((_item, index) => (
          <Skeleton key={`skeleton_${index}`} animation="wave" height={40} />
        ))}
      </Card>
    );
  } else if (data.length === 0 || (dayTasks && Object.keys(dayTasks).length === 0)) {
    return (
      <Stack className="care-table-empty" justifyContent="center" alignItems="center">
        <h3 className="care-table-empty-title">{t(translations.office.pending_tasks.empty)}</h3>
      </Stack>
    );
  }
  return (
    <Card
      sx={{
        maxHeight: `calc(100vh - ${topSpacing}px)`,
      }}
      elevation={0}
      className="pending-tasks-card"
    >
      <>
        <Alert severity="info">
          <Trans>{t(translations.office.pending_tasks.info)}</Trans>
        </Alert>
        <Box className="select-all">
          <CustomCheckbox
            checked={Object.keys(tasks).length === selectedTasks.length}
            onChange={handleSelectAll}
          />
          <Typography>{t(translations.office.pending_tasks.all)}</Typography>
        </Box>
        {Object.keys(dayTasks).map((day) => {
          countTasks = countTasks + dayTasks[day].length;
          return (
            <Box key={`pending-task-day-${day}`} className="pending-task-day">
              <Box className="pending-task-day-check">
                <CustomCheckbox
                  checked={
                    (dayTasks[day] || []).length ===
                    selectedTasks.filter((id) =>
                      (dayTasks[day] || []).map((task) => task.id).includes(id)
                    ).length
                  }
                  onChange={(event) => handleSelectDay(event, day)}
                />
              </Box>
              <CardCollapse
                cardProps={{
                  className: 'pending-task-day-card',
                }}
                defaultExpand={countTasks <= 25}
                collapsableHeader={
                  <Typography className="pending-task-header-day" noWrap>
                    {day}
                  </Typography>
                }
              >
                {dayTasks[day] &&
                  dayTasks[day].map((task) => {
                    const firstFilteredSocialRes = filters ? filters[0] : '';
                    const filteredSocialRes = task.socialResponses.find(
                      (response) => response === firstFilteredSocialRes
                    );
                    const avatarBorderColor =
                      filteredSocialRes && socialResponses[filteredSocialRes]?.foregroundColor
                        ? socialResponses[filteredSocialRes]?.foregroundColor
                        : socialResponses[task.socialResponses[0]]?.foregroundColor
                          ? socialResponses[task.socialResponses[0]]?.foregroundColor
                          : 'transparent';
                    return (
                      <Box key={`pending-task-${task.id}`} className="pending-task">
                        <CustomCheckbox
                          checked={selectedTasks.includes(task.id)}
                          onChange={(event) => {
                            if (event.target.checked) {
                              setSelectedTasks([...selectedTasks, task.id]);
                            } else {
                              setSelectedTasks(selectedTasks.filter((id) => id !== task.id));
                            }
                          }}
                        />
                        <ButtonBase
                          onClick={() => onPendingClick(task.id)}
                          className="pending-task-container"
                        >
                          <Box className="pending-task-content">
                            <Avatar
                              sx={{
                                border: `1px solid ${avatarBorderColor}`,
                              }}
                              className="pending-task-avatar"
                              src={task.picture}
                            />
                            <Typography className="pending-task-hour">
                              {moment(task.date).format('HH:mm')}
                            </Typography>
                            <Typography className="pending-task-group">{task.group}</Typography>
                            <Typography className="pending-task-type">{task.type}</Typography>
                          </Box>
                          <Box className="pending-task-check">
                            <CheckIcon />
                            <Typography>
                              {t(translations.daily_care_log.task_details.task_done)}
                            </Typography>
                          </Box>
                        </ButtonBase>
                      </Box>
                    );
                  })}
              </CardCollapse>
            </Box>
          );
        })}
        <Box className="pending-buttons">
          <Button
            onClick={onCancel}
            size="large"
            color="error"
            className="pending-button-cancel"
            disabled={!from}
          >
            {t(translations.office.generics.cancel)}
          </Button>
          <Button
            onClick={() => onValidateAllTasks(tasks, selectedTasks)}
            size="large"
            variant="contained"
            color="primary"
            className="pending-button-validate"
            disabled={selectedTasks.length === 0 || !canWriteSelected}
          >
            {t(translations.office.pending_tasks.validate)}
          </Button>
        </Box>
      </>
    </Card>
  );
}
